import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { Button } from '../components/ui/Button';
import searchIcon from '../assets/images/search.png';
import ledgerFolderIcon from '../assets/images/ledger_folder.png';
import ledgerFolderIconDark from '../assets/images-night/ledger_folder.png';
import stockValuationFolderIcon from '../assets/images/stock_valuations_folder.png';
import stockValuationFolderIconDark from '../assets/images-night/stock_valuations_folder.png';
import overallStockIcon from '../assets/images/overall_stock.png';
import overallStockIconDark from '../assets/images-night/overall_stock.png';
import tradingFolderIcon from '../assets/images/trading_folder.png';
import tradingFolderIconDark from '../assets/images-night/trading_folder.png';
import overallTradingIcon from '../assets/images/overall_trading.png';
import overallTradingIconDark from '../assets/images-night/overall_trading.png';
import profitLossIcon from '../assets/images/profit_and_loss.png';
import profitLossIconDark from '../assets/images-night/profit_and_loss.png';
import balanceSheetIcon from '../assets/images/balance_sheet.png';
import balanceSheetIconDark from '../assets/images-night/balance_sheet.png';

interface AccountItem {
  id: number;
  name: string;
  description: string;
  icon: string;
  type: 'folder' | 'file';
  route: string;
}

const AccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const { setNavLinks } = useNavLinksContext();
  const { isDark } = useThemeContext();

  // Get icon based on theme
  const getIcon = (lightIcon: string, darkIcon: string) => {
    return isDark ? darkIcon : lightIcon;
  };

  // Account items data with theme-aware icons
  const accountItems: AccountItem[] = [
    {
      id: 1,
      name: 'Ledgers',
      description: 'View and manage all ledger accounts',
      icon: getIcon(ledgerFolderIcon, ledgerFolderIconDark),
      type: 'folder',
      route: '/records/accounts/ledgers'
    },
    {
      id: 2,
      name: 'Stock Valuation',
      description: 'Manage stock inventory valuation',
      icon: getIcon(stockValuationFolderIcon, stockValuationFolderIconDark),
      type: 'folder',
      route: '/records/accounts/stock-valuation'
    },
    {
      id: 3,
      name: 'Overall Stock Valuation',
      description: 'Summary of total stock valuation',
      icon: getIcon(overallStockIcon, overallStockIconDark),
      type: 'file',
      route: '/records/accounts/overall-stock-valuation'
    },
    {
      id: 4,
      name: 'Trading Accounts',
      description: 'Manage trading accounts',
      icon: getIcon(tradingFolderIcon, tradingFolderIconDark),
      type: 'folder',
      route: '/records/accounts/trading-accounts'
    },
    {
      id: 5,
      name: 'Overall Trading Account',
      description: 'Summary of trading account',
      icon: getIcon(overallTradingIcon, overallTradingIconDark),
      type: 'file',
      route: '/records/accounts/overall-trading-account'
    },
    {
      id: 6,
      name: 'Profit and Loss',
      description: 'Profit and loss statement',
      icon: getIcon(profitLossIcon, profitLossIconDark),
      type: 'file',
      route: '/records/accounts/profit-loss'
    },
    {
      id: 7,
      name: 'Balance Sheets',
      description: 'Balance sheet statements',
      icon: getIcon(balanceSheetIcon, balanceSheetIconDark),
      type: 'file',
      route: '/records/accounts/balance-sheets'
    },
  ];

  // Set navigation links for the Accounts page
  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Records' },
      ],
    });
  }, [setNavLinks]);

  const handleAccountClick = (account: AccountItem) => {
    console.log('Navigating to:', account.route);
    navigate(account.route);
  };

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="bg-light rounded-t-lg">
        {/* Header Area */}
        <div className="px-6 pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
            <div className="w-full">
              <h3 className="text-xl">Accounts</h3>
              <p className="text-grey_dark mt-1 text-sm">
                Manage financial accounts, ledgers, and statements
              </p>
            </div>
          </div>
          
        </div>

        {/* Main Content Area - Simple Grid */}
        <hr className="flex-grow border-t border-secordary m-4" />
        <div className="pt-0 px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {accountItems.map((account) => (
              <div
                key={account.id}
                onClick={() => handleAccountClick(account)}
                className="p-4 bg-light border border-grey_light rounded-lg hover:border-secondary cursor-pointer transition-all duration-200 group"
              >
                {/* Large Icon Container */}
                <div className="flex flex-col items-center justify-center mb-4">
                  <div className="w-24 h-24 flex items-center justify-center mb-4">
                    <img
                      src={account.icon}
                      alt={account.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  {/* Account Name and Description */}
                  <div className="text-center">
                    <h5 className="font-medium text-dark mb-2 transition-colors duration-200">
                      {account.name}
                    </h5>
                    <p className="text-sm text-grey_dark">
                      {account.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-light rounded-lg p-4 sm:p-6">
          <h4 className="text-lg mb-4">About Accounts</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-medium mb-2">Account Types</h5>
              <div className="space-y-2 text-sm text-grey_dark">
                <div><span className="font-medium text-dark">Ledgers:</span> Individual account records for tracking financial transactions</div>
                <div><span className="font-medium text-dark">Stock Valuation:</span> Inventory management and stock valuation reports</div>
                <div><span className="font-medium text-dark">Trading Accounts:</span> Buy/sell transaction records and summaries</div>
                <div><span className="font-medium text-dark">Financial Statements:</span> Annual reports including profit & loss and balance sheets</div>
              </div>
            </div>
            <div>
              <h5 className="font-medium mb-2">Quick Navigation</h5>
              <div className="space-y-2 text-sm text-grey_dark">
                <div>• Click on any account card to open it</div>
                <div>• Folders contain lists of related items</div>
                <div>• Files are single documents or reports</div>
                <div>• All accounts are accessible from this page</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountsPage;